<template>
  <div class="animated fadeIn">
    <div v-if="initializing" class="text-center">
      <font-awesome-icon spin icon="circle-notch" class="text-moremuted display-2"/>
    </div>

    <template v-else>
      <b-alert :show="errors.any()" variant="danger" class="px-4">
        <ul class="m-0 px-1">
          <li v-for="err in errors.all()" :key="err">
            {{ err }}
          </li>
        </ul>
      </b-alert>

      <b-row>
        <b-col lg="6" class="mb-3">
          <b-card :header="$t('shared.identityData')" class="mb-0 h-100">
            <b-form-group>
              <b-input-group>
                <b-input-group-prepend is-text>
                  {{ $t('shared.companyName') }}
                  <required-symbol/>
                </b-input-group-prepend>

                <b-form-input
                  dir="auto"
                  :name="$t('shared.companyName')"
                  v-validate="'required'"
                  v-model="model.name"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group>
              <b-input-group>
                <b-input-group-prepend is-text>
                  {{ $t('shared.nationalId') }}
                  <required-symbol/>
                </b-input-group-prepend>

                <b-form-input
                  dir="ltr"
                  :name="$t('shared.nationalId')"
                  v-validate="'required'"
                  v-model="model.nationalId"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group key="economicalCode">
              <b-input-group :prepend="$t('shared.economicalCode')">
                <b-form-input
                  dir="ltr"
                  :name="$t('shared.economicalCode')"
                  v-validate="'digits:12'"
                  v-model="model.economicalCode"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group>
              <b-input-group :prepend="$t('shared.regNum')">
                <b-form-input v-model="model.regNum" dir="ltr"/>
              </b-input-group>
            </b-form-group>

            <b-form-group>
              <date-picker
                disable-dates-after-today
                :label="$t('shared.regDate')"
                v-model="model.regDate"
              />
            </b-form-group>

            <b-form-group>
                <b-input-group :prepend="$t('couriers.countryCode')">
                  <b-form-input
                    dir="ltr"
                    :name="$t('couriers.countryCode')"
                    v-validate="'required|max:20'"
                    v-model="model.code"
                  />
                </b-input-group>
              </b-form-group>

            <b-form-group>
              <uploader :label="$t('shared.logo')" v-model="model.image"/>
            </b-form-group>

            <b-form-group class="mb-0">
              <uploader :label="$t('shared.seal')" v-model="model.seal"/>
            </b-form-group>
          </b-card>
        </b-col>

        <b-col lg="6" class="mb-3">
          <b-card :header="$t('couriers.officialData')" class="mb-0 h-100">
            <b-form-group>
              <b-input-group>
                <b-input-group-prepend is-text>
                  {{ $t('couriers.officialPhoneCode') }}
                  <required-symbol/>
                </b-input-group-prepend>

                <b-form-input
                  dir="ltr"
                  autocomplete="off"
                  :name="$t('couriers.officialPhoneCode')"
                  v-validate="'required|numeric|max:4'"
                  v-model="model.officialPhoneCode"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group>
              <b-input-group>
                <b-input-group-prepend is-text>
                  {{ $t('couriers.officialPhoneNumber') }}
                  <required-symbol/>
                </b-input-group-prepend>

                <b-form-input
                  dir="ltr"
                  autocomplete="off"
                  :name="$t('couriers.officialPhoneNumber')"
                  v-validate="'required|numeric|max:8'"
                  v-model="model.officialPhoneNumber"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group>
              <b-input-group>
                <b-input-group-prepend is-text>
                  {{ $t('shared.email') }}
                  <required-symbol/>
                </b-input-group-prepend>

                <b-form-input
                  dir="ltr"
                  placeholder="email@address.com"
                  autocomplete="off"
                  :name="$t('shared.email')"
                  v-validate="'required|email|max:50'"
                  v-model="model.officialEmail"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group>
              <auto-complete
                :prepend="$t('provinces.province')"
                :select="provinceSelect"
                :search="provinceSearch"
                :placeholder="$t('provinces.enterProvince')"
                :name="$t('provinces.province')"
                v-validate="'required'"
                v-model="model.officialProvinceId"
                required
              />
            </b-form-group>

            <b-form-group>
              <auto-complete
                :disabled="!model.officialProvinceId"
                :prepend="$t('cities.city')"
                :select="citySelect"
                :search="citySearch"
                :placeholder="$t('cities.enterCity')"
                :name="$t('cities.city')"
                v-validate="'required'"
                v-model="model.officialCityId"
                required
              />
            </b-form-group>

            <b-form-group>
              <b-input-group>
                <b-input-group-prepend is-text>
                {{ $t('account.address') }}
                <required-symbol/>
                </b-input-group-prepend>

                <b-textarea
                  :rows="3"
                  v-model="model.officialAddress"
                  :name="$t('account.address')"
                  v-validate="'required|max:200'"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group>
              <b-input-group>
                <b-input-group-prepend is-text>
                  {{ $t('account.zipCode') }}
                  <required-symbol/>
                </b-input-group-prepend>

                <b-form-input
                  dir="ltr"
                  :name="$t('account.zipCode')"
                  v-validate="'required|digits:10'"
                  v-model="model.officialZipCode"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group>
              <b-input-group>
                <b-input-group-prepend is-text>
                  {{ $t('couriers.operatorName') }}
                  <required-symbol/>
                </b-input-group-prepend>

                <b-form-input
                  dir="auto"
                  :name="$t('couriers.operatorName')"
                  v-validate="'required|max:50'"
                  v-model="model.operatorName"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group class="mb-0">
              <b-input-group>
                <b-input-group-prepend v-if="$dir.ltr" is-text>
                  {{ $t('couriers.operatorCellNumber') }}
                  <required-symbol/>
                </b-input-group-prepend>

                <b-input-group-prepend is-text>
                  <template v-if="$dir.ltr">
                    {{ $config.country.phoneCode }}
                  </template>

                  <template v-else>
                    {{ $t('couriers.operatorCellNumber') }}
                    <required-symbol/>
                  </template>
                </b-input-group-prepend>

                <b-form-input
                  v-model="model.operatorCellNumber"
                  autocomplete="off"
                  dir="ltr"
                  placeholder= "912xxxxxxx"
                  :name="$t('couriers.operatorCellNumber')"
                  v-validate="'required|digits:10'"
                />

                <b-input-group-append v-if="$dir.rtl" is-text dir="ltr">
                  {{ $config.country.phoneCode }}
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-card>
        </b-col>
      </b-row>

      <div v-if="filteredDocuments && filteredDocuments.length" class="mb-3">
        <documents-editor :title="$t('shared.documents.documents')" v-model="filteredDocuments"/>
      </div>

      <b-card bg-variant="light">
        <b-row class="align-items-center">
          <b-col lg="auto" class="mb-3 mb-lg-0">
            <b-btn block variant="success" :disabled="progressing" @click="submit">
              <font-awesome-icon
                :icon="progressing ? 'cog' : 'check'"
                :spin="progressing"
                class="align-middle"
                :class="{'mr-1': $dir.ltr, 'ml-1': $dir.rtl}"
              />

              {{ $t('shared.save') }}
            </b-btn>
          </b-col>

          <b-col lg="auto" class="mb-3 mb-lg-0">
            <b-btn block variant="success" :disabled="progressing" @click="submitAndReviewRequest">
              <font-awesome-icon
                :icon="progressing ? 'cog' : 'check'"
                :spin="progressing"
                class="align-middle"
                :class="{'mr-1': $dir.ltr, 'ml-1': $dir.rtl}"
              />

              {{ $t('shared.saveAndReviewRequest') }}
            </b-btn>
          </b-col>

          <b-col v-if="status" lg="auto" :class="{ 'ml-auto': $dir.ltr, 'mr-auto': $dir.rtl }">
            <span class="text-muted">
              {{ $t('shared.status') }} :
            </span>

            <font-awesome-icon
              :icon="status.icon"
              class="align-middle mx-1"
              :class="`text-${status.variant}`"
            />
            {{ status.text }}
          </b-col>
        </b-row>
      </b-card>
    </template>
  </div>
</template>

<script>
import Breadcrumb from '@/mixins/breadcrumb'
import AutoComplete from '@/components/shared/AutoComplete'
import DatePicker from '@/components/shared/DatePicker'
import DocumentsEditor from './DocumentsEditor'
import Settings from '@/mixins/settings/index'
import Uploader from '@/components/shared/Uploader'

export default {
  name: 'BasicInfo',
  components: {
    AutoComplete,
    DatePicker,
    DocumentsEditor,
    Uploader
  },
  mixins: [Breadcrumb, Settings],
  data () {
    return {
      initializing: true,
      progressing: false,
      model: {
        name: null,
        image: null,
        nationalId: null,
        economicalCode: null,
        regNum: null,
        regDate: null,
        seal: null,
        contractExpireDate: null,
        contractNum: null,
        contractScan: null,
        officialPhoneCode: null,
        officialPhoneNumber: null,
        officialZipCode: null,
        officialProvinceId: null,
        officialCityId: null,
        officialAddress: null,
        officialEmail: null,
        operatorName: null,
        operatorCellNumber: null,
        code: null,
        status: null
      },
      documents: [],
      statuses: [
        { value: 0, text: this.$t('couriers.statuses.filling'), icon: 'pencil-alt', variant: 'info' },
        { value: 1, text: this.$t('couriers.statuses.pending'), icon: 'user-clock', variant: 'muted' },
        { value: 2, text: this.$t('couriers.statuses.approved'), icon: 'check-circle', variant: 'success' },
        { value: 3, text: this.$t('couriers.statuses.disapproved'), icon: 'times-circle', variant: 'danger' }
      ]
    }
  },
  computed: {
    status () {
      return this.statuses.find(o => o.value === this.model.status)
    },
    filteredDocuments () {
      const documentTypes = this.getCourierDocuments()
      return documentTypes && documentTypes.length ? this.documents.filter(i => documentTypes.find(j => j.type === i.type)) : null
    }
  },
  watch: {
    'model.officialProvinceId' (val, oldVal) {
      if (!this.initializing && val !== oldVal) this.model.officialCityId = null
    }
  },
  mounted () {
    this.setBreadcrumb()
    Promise.all([
      this.getModel(),
      this.getDocuments()
    ])
    .then(() => {
      this.getSettings([`${this.$config.auth.userType}Profile`, `${this.$config.auth.userType}Account`])
      .then(() => {
        this.cleanupDocuments()
        this.initializing = false
      })
    })
    .catch(err => {
      this.fail(err)
      this.goto('/account/profile')
    })
  },
  methods: {
    setBreadcrumb () {
      const items = []
      let path = ''

      path += '/account'
      items.push({ path, text: this.$t('nav.account') })

      path += '/profile/basicInfo'
      items.push({ path, text: this.$t('couriers.basicData.basicData') })

      this.commitBreadcrumb(items)
    },
    getModel () {
      return this.$api.account.getProfile({ include: 'CourierData' }, true)
      .then(res => {
        this.model.name = res.profile.courierData.name
        this.model.image = res.profile.courierData.image
        this.model.nationalId = res.profile.courierData.nationalId
        this.model.economicalCode = res.profile.courierData.economicalCode
        this.model.regNum = res.profile.courierData.regNum
        this.model.regDate = res.profile.courierData.regDate
        this.model.seal = res.profile.courierData.seal
        this.model.contractExpireDate = res.profile.courierData.contractExpireDate
        this.model.contractNum = res.profile.courierData.contractNum
        this.model.contractScan = res.profile.courierData.contractScan
        this.model.officialPhoneCode = res.profile.courierData.officialPhoneCode
        this.model.officialPhoneNumber = res.profile.courierData.officialPhoneNumber
        this.model.officialZipCode = res.profile.courierData.officialZipCode
        this.model.officialProvinceId = res.profile.courierData.officialProvinceId
        this.model.officialCityId = res.profile.courierData.officialCityId
        this.model.officialAddress = res.profile.courierData.officialAddress
        this.model.officialEmail = res.profile.courierData.officialEmail
        this.model.operatorName = res.profile.courierData.operatorName
        this.model.operatorCellNumber = res.profile.courierData.operatorCellNumber
        this.model.code = res.profile.courierData.code
        this.model.status = res.profile.courierData.status

        return this.model
      })
    },
    getDocuments () {
      return this.$api.account.getDocuments(true)
      .then(res => {
        return this.documents = res
      })
    },
    fail (err) {
      this.$showError(this.$getLocaleErrorMessage(err, 'account'))
      this.progressing = false
      return false
    },
    goto (path) {
      if (path) this.$router.push({ path })
    },
    provinceSearch (keyword, count) {
      return this.$api.provinces.getInfos({
        name: keyword,
        limit: count,
        sort: 'sortOrder-asc'
      })
      .then(items => {
        return items.map(item => {
          return {
            text: item.name,
            value: item.id
          }
        })
      })
    },
    provinceSelect (value) {
      return this.$api.provinces.getInfos({
        id: value
      })
      .then(items => {
        return {
          text: items[0].name,
          value: items[0].id
        }
      })
    },
    citySearch (keyword, count) {
      return this.$api.cities.getInfos({
        name: keyword,
        limit: count,
        provinceId: this.model.officialProvinceId,
        sort: 'sortOrder-asc'
      })
      .then(items => {
        return items.map(item => {
          return {
            text: item.name,
            value: item.id
          }
        })
      })
    },
    citySelect (value) {
      return this.$api.cities.getInfos({
        id: value
      })
      .then(items => {
        return {
          text: items[0].name,
          value: items[0].id
        }
      })
    },
    cleanupDocuments () {
      const documentTypes = this.getCourierDocuments()

      this.documents = this.documents.filter(item => {
        return documentTypes && documentTypes.length && documentTypes.find(i => i.type === item.type)
      })

      const vm = this
      function push (found, array, item) {
        if (found) {
          found.required = item.required
        } else {
          array.push({
            id: -String(Math.floor(Math.random() * 99999999)),
            type: item.type,
            name: vm.$t(`shared.documents.${item.type}`),
            attachments: null,
            expireDate: null,
            approved: false,
            required: item.required
          })
        }
      }

      if (documentTypes && documentTypes.length) {
        documentTypes.forEach(item => {
          const found = this.documents.find(i => i.type === item.type)
          push(found, this.documents, item)
        })
      }
    },
    preSubmit () {
      return Promise.all([
        this.$validator.validateAll(),
        this.$refs.documentsEditor ? this.$refs.documentsEditor.validateAll() : true
      ])
      .then(all => {
        const isValid = all[0] && all[1]
        this.progressing = isValid
        return isValid
      })
    },
    _submit () {
      this.progressing = true
      const data = Object.assign({}, this.model)

      return this.$api.account.updateCourierData(data, true)
      .then(res => {
        this.model.image = res.image
        this.model.seal = res.seal
        this.model.status = res.status

        return this.$api.account.updateDocuments(this.documents, true)
        .then(res => {
          this.documents = res
        })
      })
    },
    reviewRequest () {
      return this.$api.account.courierDataApprovementRequest(false)
    },
    submit () {
      this.preSubmit()
      .then(proceed => {
        if (proceed) {
          this.progressing = true
          return this._submit()
          .then(() => {
            return this.$showSuccess(this.$t('account.profileUpdated'))
          })
        }
      })
      .catch(err => {
        return this.$showError(this.$getLocaleErrorMessage(err, 'couriers'))
      })
      .then(() => { this.progressing = false })
    },
    submitAndReviewRequest () {
      this.preSubmit()
      .then(proceed => {
        if (proceed) {
          this.progressing = true
          return this._submit()
          .then(this.reviewRequest)
          .then(() => {
            this.model.status = 1
            this.$showSuccess(`${this.$t('account.profileUpdated')} ${this.$t('couriers.waitForAdminApproval')}`)
          })
        }
      })
      .catch(err => {
        return this.$showError(this.$getLocaleErrorMessage(err, 'couriers'))
      })
      .then(() => { this.progressing = false })
    }
  }
}
</script>
